import { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Space,
  Typography,
  DatePicker,
  Button,
  message,
  Statistic,
  Row,
  Col,
  Empty
} from 'antd';
import { DollarOutlined, CalendarOutlined } from '@ant-design/icons';
import { PageHeader } from '../components/PageHeader';
import { api, ProfitAllocation } from '../api';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;
const { Text } = Typography;

type DatePreset = '7d' | '30d' | '90d' | 'all';

const AllocationHistory = () => {
  const [loading, setLoading] = useState(false);
  const [allocations, setAllocations] = useState<ProfitAllocation[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [pageTotals, setPageTotals] = useState({
    gross_share_cents: 0,
    company_cut_cents: 0,
    net_share_cents: 0,
  });
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [activePreset, setActivePreset] = useState<DatePreset>('30d');

  useEffect(() => {
    // Set default to last 30 days
    handlePresetClick('30d');
  }, []);

  const fetchAllocations = async (page = 1, from?: string, to?: string) => {
    try {
      setLoading(true);
      const data = await api.allocations.getMyAllocations({
        from,
        to,
        page,
        per: pagination.pageSize,
      });

      setAllocations(data.allocations);
      setPagination({
        current: page,
        pageSize: data.pagination.per_page,
        total: data.pagination.total_count,
      });
      setPageTotals(data.page_totals);
    } catch (error: any) {
      console.error('Failed to fetch allocations:', error);
      message.error('Failed to load allocation history');
    } finally {
      setLoading(false);
    }
  };

  const handlePresetClick = (preset: DatePreset) => {
    setActivePreset(preset);
    const now = dayjs();
    let from: Dayjs | null = null;
    let to: Dayjs | null = now;

    switch (preset) {
      case '7d':
        from = now.subtract(7, 'days');
        break;
      case '30d':
        from = now.subtract(30, 'days');
        break;
      case '90d':
        from = now.subtract(90, 'days');
        break;
      case 'all':
        from = null;
        to = null;
        break;
    }

    setDateRange([from, to]);
    fetchAllocations(
      1,
      from?.format('YYYY-MM-DD'),
      to?.format('YYYY-MM-DD')
    );
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates) {
      setDateRange(dates);
      setActivePreset('all'); // Clear preset when custom range selected
      fetchAllocations(
        1,
        dates[0]?.format('YYYY-MM-DD'),
        dates[1]?.format('YYYY-MM-DD')
      );
    }
  };

  const handleTableChange = (newPagination: any) => {
    fetchAllocations(
      newPagination.current,
      dateRange[0]?.format('YYYY-MM-DD'),
      dateRange[1]?.format('YYYY-MM-DD')
    );
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'profit_entry_date',
      key: 'entry_date',
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
      width: 120,
      fixed: 'left' as const,
    },
    {
      title: 'Entry ID',
      dataIndex: 'profit_entry_id',
      key: 'entry_id',
      render: (id: string) => <Text copyable>{id?.substring(0, 8)}</Text>,
      width: 100,
    },
    {
      title: 'Ownership %',
      dataIndex: 'ownership_percent',
      key: 'ownership_percent',
      render: (percent: string) => `${parseFloat(percent).toFixed(2)}%`,
      width: 120,
      align: 'right' as const,
    },
    {
      title: 'Gross Share',
      dataIndex: 'gross_share_cents',
      key: 'gross_share',
      render: (cents: number) => formatCurrency(cents),
      width: 130,
      align: 'right' as const,
    },
    {
      title: 'Fees',
      key: 'company_cut',
      width: 150,
      render: (_: any, record: ProfitAllocation) => (
        <Space orientation="vertical" size={0}>
          <Text>{(Number(record.company_cut_percent) * 100).toFixed(1)}%</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {formatCurrency(record.company_cut_cents)}
          </Text>
        </Space>
      ),
      align: 'right' as const,
    },
    {
      title: 'Net Share',
      dataIndex: 'net_share_cents',
      key: 'net_share',
      render: (cents: number) => (
        <Text strong style={{ color: '#52c41a' }}>
          {formatCurrency(cents)}
        </Text>
      ),
      width: 130,
      align: 'right' as const,
    },
  ];

  return (
    <>
      <PageHeader
        title="Allocation History"
        description="View your profit allocation history and earnings breakdown"
      />

      <Card>
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          {/* Date Filter Controls */}
          <Space wrap size="small">
            <Button
              type={activePreset === '7d' ? 'primary' : 'default'}
              onClick={() => handlePresetClick('7d')}
              icon={<CalendarOutlined />}
              size="small"
            >
              7D
            </Button>
            <Button
              type={activePreset === '30d' ? 'primary' : 'default'}
              onClick={() => handlePresetClick('30d')}
              icon={<CalendarOutlined />}
              size="small"
            >
              30D
            </Button>
            <Button
              type={activePreset === '90d' ? 'primary' : 'default'}
              onClick={() => handlePresetClick('90d')}
              icon={<CalendarOutlined />}
              size="small"
            >
              90D
            </Button>
            <Button
              type={activePreset === 'all' ? 'primary' : 'default'}
              onClick={() => handlePresetClick('all')}
              size="small"
            >
              All
            </Button>
            <RangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              format="MMM D, YYYY"
              style={{ maxWidth: '100%' }}
              size="small"
            />
          </Space>

          {/* Page Totals */}
          {allocations.length > 0 && (
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="Page Gross Share"
                    value={pageTotals.gross_share_cents / 100}
                    precision={2}
                    prefix={<DollarOutlined />}
                    styles={{ content: { color: '#1890ff' } }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="Page Fees"
                    value={pageTotals.company_cut_cents / 100}
                    precision={2}
                    prefix={<DollarOutlined />}
                    styles={{ content: { color: '#faad14' } }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card size="small">
                  <Statistic
                    title="Page Net Share"
                    value={pageTotals.net_share_cents / 100}
                    precision={2}
                    prefix={<DollarOutlined />}
                    styles={{ content: { color: '#52c41a' } }}
                  />
                </Card>
              </Col>
            </Row>
          )}

          {/* Allocations Table */}
          <Table
            columns={columns}
            dataSource={allocations}
            loading={loading}
            pagination={pagination}
            onChange={handleTableChange}
            rowKey="id"
            scroll={{ x: 900 }}
            locale={{
              emptyText: (
                <Empty
                  description="No allocation history found for the selected date range"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
          />
        </Space>
      </Card>
    </>
  );
};

export default AllocationHistory;
